import React, { useState, useMemo } from 'react'
import I18n from 'i18n'
import classNames from 'classnames'
import DatePicker from 'react-datepicker'

import _ from 'lodash'

import TableInteractive from './TableInteractive'
import { WithBackend } from '../../../backend/BackendProvider'
import { COACH_DASHBOARD_HOME_CATEGORIES, datePickerDefaultOptions } from '../../../common/Constants'
import { createAthleteRowEntry } from './CoachDashboardHelper'
import Toggle from '../../../atomic/atoms/Toggle.tsx'
import { MobileTable } from 'components/analysis/dashboards/coach_individual/MobileTable'
import SpinnerWrapper from 'components/common/SpinnerWrapper'
import { useTrainingSchedules } from '../../../training/context/TrainingSchedulesContext'

const CoachHomeTab = (props) => {
  const {
    athletesEntryData, titles, day, onDayChange, onAthleteClick,
    excludeAdmins, setExcludeAdmins, groupId, state
  } = props

  const { getSchedulesForAthleteAndDay } = useTrainingSchedules()

  // State for z-score color coding toggle (default: on)
  const [zScoreColoringEnabled, setZScoreColoringEnabled] = useState(true)

  // returns sorted array
  // the more properties inside 'datasets' property that are not undefined or null,
  // the higher in the array the athlete is sorted.
  // meaning the more data (for the table) an athlete has, the higher in the list they will appear.
  function sortAthletesObject (athletes) {
    return _.sortBy(athletes, function (athlete) {
      return _.filter(Object.values(athlete.datasets), val => val !== undefined && val !== null).length
    }).reverse()
  }

  const sortedAthletesArray = sortAthletesObject(athletesEntryData)
  // Make sure array order is retained in object by making array index id (instead of athleteId).
  const sortedAthletesObject = _.fromPairs(_.map(sortedAthletesArray, (athlete, index) => [index, athlete]))

  const athleteEntries = useMemo(() => {
    if (state === 'noData' || state === 'isFetching') return {}
    return _.mapValues(sortedAthletesObject, (entryData) => {
      const { datasets, profile } = entryData

      // Get direct scheduled training data from context (fallback to empty array if context not ready)
      let directScheduledTrainings = []
      try {
        directScheduledTrainings = getSchedulesForAthleteAndDay(profile.id, day) || []
      } catch (error) {
        console.warn('Not enough data!', error)
      }

      return createAthleteRowEntry(
        profile,
        datasets,
        onAthleteClick,
        directScheduledTrainings,
        day,
        (zScoreColoringEnabled && state === 'ready')
      )
    })
  }, [sortedAthletesObject, onAthleteClick, zScoreColoringEnabled, day, state, getSchedulesForAthleteAndDay])

  const hasGroup = !!groupId
  const noData = hasGroup && state === 'noData'
  const ready = state !== 'isFetching'

  return (
    <>
      <div className='row'>
        <div className='col s12 input-field'>
          <DatePicker
            id='home-tab-date-picker' {...datePickerDefaultOptions()} selected={day} onChange={onDayChange}
          />
          <label htmlFor='home-tab-date-picker' className={classNames({ active: day })}>{I18n.t('components.dashboards.coach_individual.home.select_day')}</label>
        </div>
      </div>
      <div className='row'>
        <div className='col s12'>
          <Toggle id='exclude-admins-toggle' title={I18n.t('components.dashboards.coach_individual.home.toggle')} onChange={setExcludeAdmins} isChecked={excludeAdmins} />
        </div>
      </div>
      <div className='row'>
        <div className='col s12'>
          <Toggle
            id='zscore-coloring-toggle'
            title={I18n.t('components.dashboards.coach_individual.home.zscore_toggle')}
            onChange={() => setZScoreColoringEnabled(!zScoreColoringEnabled)}
            isChecked={zScoreColoringEnabled}
          />
        </div>
      </div>
      <div className='row'>
        <div className='col s12'>
          <div className='titlebar'>{
            excludeAdmins
              ? I18n.t('components.dashboards.coach_individual.home.banner.athletes')
              : I18n.t('components.dashboards.coach_individual.home.banner.members')
          }
          </div>

          {!hasGroup && (
            <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
              <p>{I18n.t('components.dashboards.coach_individual.home.select_group_prompt')}</p>
            </div>
          )}

          {noData && (
            <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
              <p>{I18n.t('components.dashboards.coach_individual.home.no_data')}</p>
            </div>
          )}

          {hasGroup && !noData && (
            <SpinnerWrapper ready={ready} transparent={false}>
              <>
                {state === 'zScoring' && zScoreColoringEnabled && (
                  <div className='helper-text' style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '6px 0 10px 0' }}>
                    <span>{I18n.t('components.dashboards.coach_individual.home.zscores_calculating')}</span>
                  </div>
                )}

                <TableInteractive
                  categories={COACH_DASHBOARD_HOME_CATEGORIES}
                  titles={titles}
                  entries={athleteEntries}
                  id='home-table'
                  className='hide-on-med-and-down'
                />
                <MobileTable titles={titles} entries={athleteEntries} />
              </>
            </SpinnerWrapper>
          )}
        </div>
      </div>
    </>
  )
}

export default WithBackend(CoachHomeTab)
